import { formatBillingDateShort } from "./billingFormat";
import type { BillingUsageDailyItem } from "../../../models/billing/Billing";

/** Точка спарклайна: календарный день и потраченное за него. */
export interface BillingUsagePoint {
  /** Дата дня в формате бэка, YYYY-MM-DD. */
  date: string;
  /** Потрачено токенов за день, всегда ≥ 0. */
  value: number;
}

const BUSINESS_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;

function pad2(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}

/**
 * Календарная дата YYYY-MM-DD по локальному времени пользователя.
 * `toISOString()` не подходит: он отдаёт дату UTC, и ночью по Москве это ещё «вчера».
 */
export function toBusinessDate(date: Date): string {
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
}

/**
 * Окно «последние `days` дней включая `today`» для запроса суточного расхода.
 * Границы включительно, в формате бэка YYYY-MM-DD. Меньше одного дня окно быть не может.
 */
export function usageWindow(days: number, today: Date): { from: string; to: string } {
  const to = new Date(today);
  to.setHours(0, 0, 0, 0);
  const from = new Date(to);
  from.setDate(to.getDate() - (Math.max(1, Math.trunc(days)) - 1));
  return { from: toBusinessDate(from), to: toBusinessDate(to) };
}

/**
 * Точки спарклайна «Расход за N дней» в хронологическом порядке.
 * Ряд с бэка плотный (дни без операций приходят нулями), поэтому пропуски не
 * заполняем — только гарантируем порядок и неотрицательные числа.
 */
export function dailyUsagePoints(items: BillingUsageDailyItem[]): BillingUsagePoint[] {
  return [...items]
    .sort((a, b) => a.business_date.localeCompare(b.business_date))
    .map((item) => ({
      date: item.business_date,
      value: Math.max(0, Number(item.spent_tokens) || 0),
    }));
}

/**
 * Подпись дня для подсказки: «2026-09-12» → «12 сен».
 * Дату собираем как локальную полночь: `new Date("2026-09-12")` — это полночь UTC,
 * и западнее Гринвича она превратилась бы в предыдущий день.
 */
export function formatUsageDay(date: string): string {
  const parts = BUSINESS_DATE.exec(date);
  if (!parts) return date;
  const [, year, month, day] = parts;
  return formatBillingDateShort(new Date(Number(year), Number(month) - 1, Number(day)));
}
